// The browser store's layout (spec FR-003, FR-004, Q5, Q11). Every per-user record the market
// keeps lives in this browser's localStorage, under one prefix, namespaced by Midnight network,
// the wallet's device key (a Solana public key as 64 lowercase hex) and Passport account:
//
//   night-market/schema                                          the schema version (an integer)
//   night-market/v1/_global/<kind>                               settings for this browser
//   night-market/v1/<network>/<device key>/-/<kind>[/<id>]       a wallet's records with no account
//   night-market/v1/<network>/<device key>/<account>/<kind>[/<id>]
//
// Each value is JSON: {"v": 1, "kind", "updatedAt" (ms), "data"}. Records of a SENSITIVE kind
// (the account's encryption secret) are masked in the Local Data tab until revealed.

import { z } from 'zod';

export const STORE_PREFIX = 'night-market/';
export const SCHEMA_KEY = 'night-market/schema';
export const SCHEMA_VERSION = 1;
const V1 = 'night-market/v1/';

// AA 00060: `bridge` keeps the account's Bridge in (and later Bridge out) records; no secret.
export const RECORD_KINDS = [
  'profile',
  'account',
  'secret',
  'coins',
  'roster',
  'offer',
  'job',
  'settings',
  'bridge',
] as const;
export type RecordKind = (typeof RECORD_KINDS)[number];

/** Kinds whose value is a secret: masked until the customer reveals it. */
export const SENSITIVE_KINDS: ReadonlySet<RecordKind> = new Set(['secret']);

/** The asset filter (plan 00042, ../assets/filter.ts): the assets this browser's pages show,
 *  `{"assets": ["USDC", …]}`; absent means every asset. A browser-wide `settings` record. */
export const ASSET_FILTER_ID = 'asset-filter';
export const ASSET_FILTER_KEY = `${V1}_global/settings/${ASSET_FILTER_ID}`;
/** Browser-wide records that travel with a wallet's Export, and that Import accepts. */
export const CARRIED_GLOBAL_KEYS: ReadonlySet<string> = new Set([ASSET_FILTER_KEY]);

export interface WalletScope {
  network: string;
  /** The wallet's device key: its Solana public key, 64 lowercase hex. */
  owner: string;
}

export type RecordScope = { global: true } | { global: false; network: string; owner: string; account: string | null };

const NETWORK_RE = /^[a-z0-9-]{1,32}$/;
const OWNER_RE = /^[0-9a-f]{64}$/;
const ACCOUNT_RE = /^[0-9a-f]{64}$/;
const ID_RE = /^[A-Za-z0-9._:-]{1,128}$/;

export class StoreKeyError extends Error {
  override name = 'StoreKeyError';
}

export function normaliseScope(scope: WalletScope): WalletScope {
  const s = { network: scope.network, owner: scope.owner.replace(/^0x/, '').toLowerCase() };
  if (!NETWORK_RE.test(s.network)) throw new StoreKeyError(`bad network "${scope.network}"`);
  if (!OWNER_RE.test(s.owner)) throw new StoreKeyError('bad wallet key');
  return s;
}

/** The localStorage key of a record. */
export function recordKey(
  scope: WalletScope | 'global',
  kind: RecordKind,
  opts: { account?: string | null; id?: string } = {},
): string {
  const id = opts.id;
  if (id !== undefined && !ID_RE.test(id)) throw new StoreKeyError(`bad record id "${id}"`);
  const tail = id === undefined ? kind : `${kind}/${id}`;
  if (scope === 'global') return `${V1}_global/${tail}`;
  const s = normaliseScope(scope);
  const account = opts.account ? opts.account.replace(/^0x/, '').toLowerCase() : '-';
  if (account !== '-' && !ACCOUNT_RE.test(account)) throw new StoreKeyError('bad account address');
  if (kind === 'bridge' && (account === '-' || id === undefined))
    throw new StoreKeyError('a bridge record needs an account and an id');
  return `${V1}${s.network}/${s.owner}/${account}/${tail}`;
}

export interface ParsedKey {
  scope: RecordScope;
  kind: RecordKind;
  id?: string;
}

/** Parse a v1 record key; null for anything that is not one. */
export function parseKey(key: string): ParsedKey | null {
  if (!key.startsWith(V1)) return null;
  const parts = key.slice(V1.length).split('/');
  const isKind = (k: string | undefined): k is RecordKind => (RECORD_KINDS as readonly string[]).includes(k ?? '');
  if (parts[0] === '_global') {
    const [, kind, id, ...rest] = parts;
    if (!isKind(kind) || rest.length > 0 || (id !== undefined && !ID_RE.test(id))) return null;
    return { scope: { global: true }, kind, ...(id !== undefined ? { id } : {}) };
  }
  const [network, owner, account, kind, id, ...rest] = parts;
  if (!network || !NETWORK_RE.test(network) || !owner || !OWNER_RE.test(owner)) return null;
  if (account !== '-' && !ACCOUNT_RE.test(account ?? '')) return null;
  if (!isKind(kind) || rest.length > 0 || (id !== undefined && !ID_RE.test(id))) return null;
  // AA 00060: a bridge record belongs to one account and has an id (`in-<signature>`); anything else
  // under that name is foreign.
  if (kind === 'bridge' && (account === '-' || id === undefined)) return null;
  return {
    scope: { global: false, network, owner, account: account === '-' ? null : account! },
    kind,
    ...(id !== undefined ? { id } : {}),
  };
}

export function inWalletScope(parsed: ParsedKey, scope: WalletScope): boolean {
  const s = normaliseScope(scope);
  return !parsed.scope.global && parsed.scope.network === s.network && parsed.scope.owner === s.owner;
}

export const StoredRecordSchema = z.object({
  v: z.literal(1),
  kind: z.enum(RECORD_KINDS),
  updatedAt: z.number().int().nonnegative(),
  data: z.unknown(),
});
export type StoredRecord<T = unknown> = { v: 1; kind: RecordKind; updatedAt: number; data: T };

export function encodeRecord<T>(kind: RecordKind, data: T, updatedAt: number): string {
  return JSON.stringify({ v: 1, kind, updatedAt, data });
}

// ── Export files (Q11) ──────────────────────────────────────────────────────

export const EXPORT_FORMAT = 'night-market-local-data';
export const EXPORT_FORMAT_VERSION = 1;

/** The most an import's records may hold (security review F-B5): their total size once
 *  serialised, key + value in UTF-16 code units, the measure localStorage itself uses, and about
 *  what it holds per site (5 MiB). So every export of this page's own records fits (F-B8). */
export const MAX_IMPORT_FILE_BYTES = 5 * 1024 * 1024;
/** The largest file Import reads at all, before parsing it (a guard against absurd files, not the
 *  export's limit). An export is written as compact JSON (`exportFileText`): its records plus a few
 *  bytes each, and at most 3 UTF-8 bytes per character, so every export whose records fit
 *  `MAX_IMPORT_FILE_BYTES` is smaller than this. Older, indented exports (about 1.4 to 2 times
 *  their records) pass too (security review F-B8). */
export const MAX_IMPORT_READ_BYTES = 4 * MAX_IMPORT_FILE_BYTES;
export const MAX_IMPORT_RECORDS = 10_000;

export const ExportFileSchema = z.object({
  format: z.literal(EXPORT_FORMAT),
  formatVersion: z.literal(EXPORT_FORMAT_VERSION),
  schemaVersion: z.number().int().positive(),
  exportedAt: z.string(),
  network: z.string().regex(NETWORK_RE),
  owner: z.string().regex(OWNER_RE),
  records: z
    .array(z.object({ key: z.string().startsWith(STORE_PREFIX).max(512), value: z.unknown() }))
    .max(MAX_IMPORT_RECORDS),
});
export type ExportFile = z.infer<typeof ExportFileSchema>;
